/**
 * Read-only typed-answer modal (FILL_IN_THE_BLANKS / REARRANGE_SENTENCE).
 * Ported from student-dashboard-v2 so the admin preview matches the student UI.
 * In preview mode the "submit" button simply advances to the next question.
 */
import React, {useEffect, useMemo, useState} from 'react';
import type {UIQuestion} from './simpleTypes';

// Pikado brand gradient (mirrors the student app's businessColor styling).
const BUSINESS_GRADIENT = 'linear-gradient(90deg, #74BBE7 0%, #F2406A 100%)';

type TypedAnswerModalProps = {
  open: boolean;
  question: UIQuestion;
  submitText?: string;
  onClose: () => void;
  onSubmit: () => void;
};

const BLANK_PATTERN = /(BLANK\d+)/g;

type RearrangeWord = {id: string; text: string};

const splitSentenceWords = (text?: string) =>
  (text ?? '')
    .trim()
    .split(/\s+/)
    .map(word => word.trim())
    .filter(Boolean);

const TypedAnswerModal = ({
  open,
  question,
  submitText = 'Submit Answer',
  onClose,
  onSubmit,
}: TypedAnswerModalProps) => {
  const blanks = useMemo(
    () => question.payload?.blanks ?? [],
    [question.payload?.blanks],
  );
  const rearrangeWords = useMemo(() => {
    const payloadChoices = blanks.flatMap(blank => blank.choices ?? []);
    const words =
      payloadChoices.length > 0
        ? payloadChoices
        : splitSentenceWords(question.questionText);
    return words.map((word, index) => ({id: `${index}-${word}`, text: word}));
  }, [blanks, question.questionText]);

  const [blankAnswers, setBlankAnswers] = useState<Record<string, string>>({});
  const [sentenceAnswer, setSentenceAnswer] = useState('');
  const [answerWords, setAnswerWords] = useState<RearrangeWord[]>([]);
  const [draggedWordId, setDraggedWordId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSentenceAnswer('');
    setAnswerWords(rearrangeWords);
    setDraggedWordId(null);
    setBlankAnswers(
      blanks.reduce<Record<string, string>>((acc, blank) => {
        acc[blank.name] = '';
        return acc;
      }, {}),
    );
  }, [open, blanks, rearrangeWords]);

  if (!open) return null;

  const isFillInTheBlanks = question.type === 'FILL_IN_THE_BLANKS';
  const isRearrangeSentence = question.type === 'REARRANGE_SENTENCE';
  const questionParts = (question.questionText ?? '').split(BLANK_PATTERN);

  const moveWordToAnswer = (
    wordId: string,
    targetIndex = answerWords.length,
  ) => {
    setAnswerWords(prev => {
      const currentIndex = prev.findIndex(item => item.id === wordId);
      const word = prev[currentIndex];
      if (!word) return prev;
      const withoutDragged = prev.filter(item => item.id !== wordId);
      const adjustedTargetIndex =
        currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
      const safeIndex = Math.max(
        0,
        Math.min(adjustedTargetIndex, withoutDragged.length),
      );
      return [
        ...withoutDragged.slice(0, safeIndex),
        word,
        ...withoutDragged.slice(safeIndex),
      ];
    });
  };

  const handleWordDragOver = (
    event: React.DragEvent<HTMLButtonElement>,
    wordId: string,
    index: number,
  ) => {
    event.preventDefault();
    if (!draggedWordId || draggedWordId === wordId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const shouldPlaceAfter = event.clientX > rect.left + rect.width / 2;
    moveWordToAnswer(draggedWordId, shouldPlaceAfter ? index + 1 : index);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-[620px] rounded-[8px] bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-[18px] font-bold text-black">
              {question.name || 'Question'}
            </div>
            {question.description && (
              <div className="mt-1 text-[13px] text-[#666666]">
                {question.description}
              </div>
            )}
          </div>
          <button
            type="button"
            className="rounded-full px-3 py-1 text-[20px] leading-none text-[#666666] hover:bg-[#f5f5f5]"
            onClick={onClose}
            aria-label="Close">
            x
          </button>
        </div>

        {isFillInTheBlanks ? (
          <div className="rounded-[8px] border border-[#eeeeee] p-4 text-[18px] leading-10 text-black">
            {questionParts.map((part, index) => {
              if (!/^BLANK\d+$/.test(part)) {
                return (
                  <React.Fragment key={`${part}-${index}`}>
                    {part}
                  </React.Fragment>
                );
              }
              return (
                <input
                  key={part}
                  value={blankAnswers[part] ?? ''}
                  onChange={event =>
                    setBlankAnswers(prev => ({
                      ...prev,
                      [part]: event.target.value,
                    }))
                  }
                  className="mx-1 inline-block w-[130px] rounded-[6px] border border-[#cccccc] px-2 py-1 text-center text-[16px] outline-none focus:border-[#F2406A]"
                  placeholder={part}
                />
              );
            })}
          </div>
        ) : isRearrangeSentence ? (
          <div>
            <div className="mb-4 rounded-[8px] border border-[#dbeafe] bg-[#eff6ff] p-4 text-[17px] text-[#1e3a8a]">
              {question.questionText}
            </div>
            <div
              className="rounded-[8px] border border-[#93c5fd] bg-white p-3 shadow-sm transition-colors duration-200"
              onDragOver={event => event.preventDefault()}
              onDrop={event => {
                event.preventDefault();
                if (draggedWordId) moveWordToAnswer(draggedWordId);
                setDraggedWordId(null);
              }}>
              <div className="mb-2 text-[13px] font-bold text-[#2563eb]">
                Arrange the sentence
              </div>
              <div className="flex min-h-[54px] flex-nowrap items-center gap-2 overflow-x-auto rounded-[6px] bg-[#eff6ff] px-2 py-2">
                {answerWords.length === 0 ? (
                  <div className="flex min-h-[38px] items-center text-[14px] text-[#60a5fa]">
                    Drag words here
                  </div>
                ) : (
                  answerWords.map((word, index) => (
                    <button
                      key={word.id}
                      type="button"
                      draggable
                      onDragStart={event => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', word.id);
                        setDraggedWordId(word.id);
                      }}
                      onDragEnd={() => setDraggedWordId(null)}
                      onDragOver={event => {
                        event.stopPropagation();
                        handleWordDragOver(event, word.id, index);
                      }}
                      onDragEnter={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (draggedWordId && draggedWordId !== word.id) {
                          handleWordDragOver(event, word.id, index);
                        }
                      }}
                      onDrop={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (draggedWordId) {
                          handleWordDragOver(event, word.id, index);
                        }
                        setDraggedWordId(null);
                      }}
                      className={`shrink-0 cursor-grab rounded-[6px] border border-[#60a5fa] bg-white px-4 py-2 text-[15px] font-bold text-[#1d4ed8] shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:border-[#2563eb] hover:bg-[#dbeafe] hover:shadow-md active:cursor-grabbing ${
                        draggedWordId === word.id ? 'scale-95 opacity-60' : ''
                      }`}>
                      {word.text}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-3 rounded-[8px] border border-[#eeeeee] bg-[#fafafa] p-4 text-[17px] text-black">
              {question.questionText}
            </div>
            <textarea
              value={sentenceAnswer}
              onChange={event => setSentenceAnswer(event.target.value)}
              className="min-h-[120px] w-full resize-none rounded-[8px] border border-[#cccccc] p-3 text-[16px] text-black outline-none focus:border-[#F2406A]"
              placeholder="Write the sentence in the correct order"
            />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-full border border-[#dddddd] px-5 py-2 text-[14px] font-bold text-[#666666]"
            onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rounded-full px-5 py-2 text-[14px] font-bold text-white"
            style={{background: BUSINESS_GRADIENT}}
            onClick={onSubmit}>
            {submitText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TypedAnswerModal;
